/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  RotateCcw,
  SlidersHorizontal,
  X,
} from 'lucide-react'

import { dashboardsService } from '@/services/dashboards'
import type {
  DashboardBoard,
  DashboardLayoutPreference,
  DashboardLayoutsResponse,
} from '@/types/dashboards'

export interface DashboardPanelDefinition {
  id: string
  label: string
}

interface DashboardCustomizationContextValue {
  board: DashboardBoard
  view: string
  panels: DashboardPanelDefinition[]
  layout: DashboardLayoutPreference
  isCustomized: boolean
  hasSavedLayout: boolean
  isLoading: boolean
  isSaving: boolean
  error: unknown
  registerPanels: (panels: DashboardPanelDefinition[]) => void
  saveLayout: (layout: DashboardLayoutPreference) => Promise<void>
  resetLayout: () => Promise<void>
  reload: () => void
}

const DashboardCustomizationContext = createContext<DashboardCustomizationContextValue | null>(null)
const NO_PANELS: DashboardPanelDefinition[] = []

export function dashboardLayoutsQueryKey(board: DashboardBoard) {
  return ['dashboard-layouts', board] as const
}

function sameList(left: string[], right: string[]) {
  return left.length === right.length && left.every((value, index) => value === right[index])
}

function samePanels(left: DashboardPanelDefinition[], right: DashboardPanelDefinition[]) {
  return left.length === right.length
    && left.every((panel, index) => panel.id === right[index]?.id && panel.label === right[index]?.label)
}

export function normalizeDashboardLayout(
  saved: DashboardLayoutPreference | undefined,
  panels: DashboardPanelDefinition[],
): DashboardLayoutPreference {
  const defaults = panels.map((panel) => panel.id)
  const known = new Set(defaults)
  const order: string[] = []
  for (const id of saved?.order ?? []) {
    if (known.has(id) && !order.includes(id)) order.push(id)
  }
  for (const id of defaults) {
    if (!order.includes(id)) order.push(id)
  }
  const hidden = (saved?.hidden ?? []).filter((id, index, values) => (
    known.has(id) && values.indexOf(id) === index
  ))
  return { order, hidden }
}

export function DashboardCustomizationProvider({
  board,
  view,
  children,
}: {
  board: DashboardBoard
  view: string
  children: ReactNode
}) {
  const queryClient = useQueryClient()
  const [registry, setRegistry] = useState<{ view: string; panels: DashboardPanelDefinition[] }>({
    view,
    panels: [],
  })
  const panels = useMemo(
    () => (registry.view === view ? registry.panels : NO_PANELS),
    [registry.panels, registry.view, view],
  )
  const query = useQuery<DashboardLayoutsResponse>({
    queryKey: dashboardLayoutsQueryKey(board),
    queryFn: ({ signal }) => dashboardsService.getLayouts(board, signal),
    staleTime: 60_000,
  })
  const { refetch } = query

  const updateCachedView = useCallback((next: DashboardLayoutPreference | null) => {
    queryClient.setQueryData<DashboardLayoutsResponse>(
      dashboardLayoutsQueryKey(board),
      (current) => {
        const layouts = { ...(current?.layouts ?? {}) }
        if (next) layouts[view] = next
        else delete layouts[view]
        return { board, layouts }
      },
    )
  }, [board, queryClient, view])

  const saveMutation = useMutation({
    mutationFn: (next: DashboardLayoutPreference) => dashboardsService.saveLayout(board, view, next),
    onSuccess: (response) => updateCachedView(response.layout),
  })
  const resetMutation = useMutation({
    mutationFn: () => dashboardsService.resetLayout(board, view),
    onSuccess: () => updateCachedView(null),
  })

  const registerPanels = useCallback((next: DashboardPanelDefinition[]) => {
    setRegistry((current) => (
      current.view === view && samePanels(current.panels, next)
        ? current
        : { view, panels: next }
    ))
  }, [view])

  const saved = query.data?.layouts[view]
  const layout = useMemo(() => normalizeDashboardLayout(saved, panels), [panels, saved])
  const defaults = panels.map((panel) => panel.id)
  const isCustomized = layout.hidden.length > 0 || !sameList(layout.order, defaults)
  const reload = useCallback(() => { void refetch() }, [refetch])
  const value = useMemo<DashboardCustomizationContextValue>(() => ({
    board,
    view,
    panels,
    layout,
    isCustomized,
    hasSavedLayout: Boolean(saved),
    isLoading: query.isLoading,
    isSaving: saveMutation.isPending || resetMutation.isPending,
    error: query.error ?? saveMutation.error ?? resetMutation.error,
    registerPanels,
    saveLayout: async (next) => { await saveMutation.mutateAsync(next) },
    resetLayout: async () => { await resetMutation.mutateAsync() },
    reload,
  }), [
    board,
    isCustomized,
    layout,
    panels,
    query.error,
    query.isLoading,
    reload,
    registerPanels,
    resetMutation,
    saveMutation,
    saved,
    view,
  ])

  return (
    <DashboardCustomizationContext.Provider value={value}>
      {children}
    </DashboardCustomizationContext.Provider>
  )
}

export function useDashboardCustomization() {
  return useContext(DashboardCustomizationContext)
}

function readableError(error: unknown) {
  const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : 'Dashboard preferences could not be saved. Please retry.'
}

function movePanel(order: string[], id: string, delta: -1 | 1) {
  const index = order.indexOf(id)
  const nextIndex = index + delta
  if (index < 0 || nextIndex < 0 || nextIndex >= order.length) return order
  const next = [...order]
  ;[next[index], next[nextIndex]] = [next[nextIndex], next[index]]
  return next
}

function viewLabel(view: string) {
  return view.replace(/-/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function DashboardCustomizeControl() {
  const customization = useDashboardCustomization()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<DashboardLayoutPreference>({ order: [], hidden: [] })
  const [message, setMessage] = useState<string | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)

  const close = useCallback(() => {
    setOpen(false)
    window.setTimeout(() => triggerRef.current?.focus(), 0)
  }, [])

  useEffect(() => {
    if (!open) return
    window.setTimeout(() => {
      dialogRef.current?.querySelector<HTMLElement>('[data-panel-row]')?.focus()
    }, 0)
  }, [open])

  if (!customization) return null

  const panelById = new Map(customization.panels.map((panel) => [panel.id, panel]))
  const orderedPanels = draft.order.map((id) => panelById.get(id)).filter(Boolean) as DashboardPanelDefinition[]
  const visibleCount = draft.order.length - draft.hidden.length
  const changed = !sameList(draft.order, customization.layout.order)
    || !sameList(draft.hidden, customization.layout.hidden)

  const togglePanel = (id: string) => {
    setMessage(null)
    setDraft((current) => {
      const isHidden = current.hidden.includes(id)
      if (!isHidden && current.order.length - current.hidden.length <= 1) {
        setMessage('At least one panel must remain visible.')
        return current
      }
      return {
        ...current,
        hidden: isHidden
          ? current.hidden.filter((panelId) => panelId !== id)
          : [...current.hidden, id],
      }
    })
  }

  const move = (id: string, delta: -1 | 1) => {
    setMessage(null)
    setDraft((current) => ({ ...current, order: movePanel(current.order, id, delta) }))
  }

  const onPanelKeyDown = (event: ReactKeyboardEvent, id: string) => {
    if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return
    event.preventDefault()
    move(id, event.key === 'ArrowUp' ? -1 : 1)
  }

  const onDialogKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      close()
      return
    }
    if (event.key !== 'Tab') return
    const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [tabindex="0"]',
    ) ?? [])
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const apply = async () => {
    setMessage(null)
    try {
      await customization.saveLayout(draft)
      close()
    } catch (error) {
      setMessage(readableError(error))
    }
  }

  const reset = async () => {
    setMessage(null)
    try {
      await customization.resetLayout()
      setDraft(normalizeDashboardLayout(undefined, customization.panels))
      setMessage('Default layout restored.')
    } catch (error) {
      setMessage(readableError(error))
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setDraft(customization.layout)
          setMessage(null)
          setOpen(true)
        }}
        aria-haspopup="dialog"
        className="flex items-center gap-1 rounded border border-stone-700 px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-stone-400 transition-colors hover:border-amber-400/30 hover:text-amber-200"
      >
        <SlidersHorizontal size={10} /> Customize
        {customization.layout.hidden.length > 0 ? (
          <span className="rounded bg-amber-400/15 px-1 text-amber-200">
            {customization.layout.hidden.length} hidden
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
          onMouseDown={(event) => { if (event.target === event.currentTarget) close() }}
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="dashboard-customize-title"
            aria-describedby="dashboard-customize-help"
            onKeyDown={onDialogKeyDown}
            className="flex max-h-[82vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-amber-400/20 bg-stone-950 shadow-2xl"
          >
            <div className="flex items-start justify-between border-b border-stone-800 px-4 py-3">
              <div>
                <h2 id="dashboard-customize-title" className="text-[13px] font-semibold text-stone-100">
                  Customize {viewLabel(customization.view)}
                </h2>
                <p id="dashboard-customize-help" className="mt-0.5 text-[10px] text-stone-500">
                  Show, hide, or move panels in reading order. Changes apply only to this dashboard view.
                </p>
              </div>
              <button type="button" onClick={close} aria-label="Close dashboard customization" className="rounded p-1 text-stone-500 hover:bg-white/5 hover:text-stone-200">
                <X size={14} />
              </button>
            </div>

            {customization.error && !message ? (
              <div className="flex items-center gap-2 border-b border-red-500/20 bg-red-500/10 px-4 py-2 text-[10px] text-red-300">
                <span className="flex-1">{readableError(customization.error)}</span>
                <button type="button" onClick={customization.reload} className="rounded border border-red-400/30 px-2 py-1">Retry</button>
              </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              {customization.isLoading && orderedPanels.length === 0 ? (
                <p className="px-2 py-4 text-center text-[11px] text-stone-500">Loading saved layout…</p>
              ) : (
                <ol className="space-y-1" aria-label="Dashboard panels">
                  {orderedPanels.map((panel, index) => {
                    const hidden = draft.hidden.includes(panel.id)
                    return (
                      <li
                        key={panel.id}
                        data-panel-row
                        tabIndex={0}
                        onKeyDown={(event) => onPanelKeyDown(event, panel.id)}
                        className="flex items-center gap-2 rounded-md border border-stone-800 bg-black/30 px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-amber-400/60"
                      >
                        <span className="w-5 text-center font-mono text-[10px] text-stone-600">{index + 1}</span>
                        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-[11px] text-stone-200">
                          <input
                            type="checkbox"
                            checked={!hidden}
                            onChange={() => togglePanel(panel.id)}
                            aria-label={`Show ${panel.label}`}
                          />
                          <span className="truncate">{panel.label}</span>
                          <span className="ml-auto flex items-center gap-1 text-[9px] text-stone-500">
                            {hidden ? <EyeOff size={10} /> : <Eye size={10} />}
                            {hidden ? 'Hidden' : 'Shown'}
                          </span>
                        </label>
                        <button
                          type="button"
                          onClick={() => move(panel.id, -1)}
                          disabled={index === 0}
                          aria-label={`Move ${panel.label} up`}
                          className="rounded p-1 text-stone-500 hover:bg-white/5 hover:text-stone-200 disabled:opacity-25"
                        >
                          <ChevronUp size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(panel.id, 1)}
                          disabled={index === orderedPanels.length - 1}
                          aria-label={`Move ${panel.label} down`}
                          className="rounded p-1 text-stone-500 hover:bg-white/5 hover:text-stone-200 disabled:opacity-25"
                        >
                          <ChevronDown size={12} />
                        </button>
                      </li>
                    )
                  })}
                </ol>
              )}
              <p className="mt-2 px-1 text-[9px] text-stone-600">
                Keyboard: focus a panel and press Alt + ↑ or Alt + ↓ to move it.
              </p>
            </div>

            <div className="border-t border-stone-800 px-4 py-3">
              <div aria-live="polite" className="mb-2 min-h-4 text-[10px] text-amber-200">
                {message ?? `${visibleCount} of ${draft.order.length} panels shown`}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void reset()}
                  disabled={customization.isSaving || !customization.hasSavedLayout}
                  className="flex items-center gap-1 rounded border border-stone-700 px-2.5 py-1.5 text-[10px] text-stone-400 hover:text-stone-200 disabled:opacity-35"
                >
                  <RotateCcw size={10} /> Reset to default
                </button>
                <button type="button" onClick={close} className="ml-auto rounded border border-stone-700 px-3 py-1.5 text-[10px] text-stone-300">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void apply()}
                  disabled={customization.isSaving || !changed}
                  className="rounded bg-amber-400 px-3 py-1.5 text-[10px] font-semibold text-stone-950 disabled:opacity-40"
                >
                  {customization.isSaving ? 'Saving…' : 'Apply layout'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
